/**
 * On-time streak tiers (NAU-247).
 *
 * The tier thresholds are real product rules and stay put once the streak day
 * count comes from the backend — only the `days` input is mocked for now.
 */

export type StreakTierKey =
  | "no_data"
  | "action_required"
  | "building_momentum"
  | "progressing_well"
  | "thriving"
  | "excellent"
  | "gold_standard";

export interface StreakTier {
  key: StreakTierKey;
  label: string;
  message: string;
  /** Visual family: drives accent colour + icon choice */
  tone: "gold" | "purple" | "blue" | "red" | "grey";
}

export function getStreakTier(days: number | null): StreakTier {
  if (days === null) {
    return {
      key: "no_data",
      label: "No data",
      message: "Streak cannot be calculated yet.",
      tone: "grey",
    };
  }
  if (days <= 0) {
    return {
      key: "action_required",
      label: "Action required",
      message:
        "A check was missed or completed too late. Let’s turn this around.",
      tone: "red",
    };
  }
  if (days <= 4) {
    return {
      key: "building_momentum",
      label: "Building momentum",
      message: "Good start! Keep it consistent.",
      tone: "blue",
    };
  }
  if (days <= 9) {
    return {
      key: "progressing_well",
      label: "Progressing well",
      message: "You’re doing well, keep going.",
      tone: "blue",
    };
  }
  if (days <= 14) {
    return {
      key: "thriving",
      label: "Thriving",
      message: "Amazing work! Keep it up.",
      tone: "purple",
    };
  }
  if (days <= 29) {
    return {
      key: "excellent",
      label: "Excellent",
      message: "Outstanding! You’re building great habits.",
      tone: "purple",
    };
  }
  return {
    key: "gold_standard",
    label: "Gold Standard",
    message:
      "An incredible achievement. Consistently keeping on top of checks.",
    tone: "gold",
  };
}
